/**
 * CycleCreateModal — minimal trainer modal to declare a new training block.
 *
 * Calm operational UX — not a metric form. Pre-fills today's date so the
 * trainer can confirm with a single click in the happy path.
 *
 * Fields:
 *   * Focus (enum)
 *   * Optional note
 *   * Start date (defaults to today)
 *   * Optional expected weeks
 */
import React, { useState } from 'react';
import { FOCUS_LABELS, FOCUS_OPTIONS } from './useCycles';

const todayIso = () => new Date().toISOString().slice(0, 10);

export const CycleCreateModal = ({ open, onClose, onCreate }) => {
  const [focus, setFocus] = useState('hypertrophy');
  const [focusNote, setFocusNote] = useState('');
  const [startDate, setStartDate] = useState(todayIso());
  const [expectedWeeks, setExpectedWeeks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onCreate({
        focus,
        focus_note: focusNote.trim() || null,
        start_date: startDate,
        expected_weeks: expectedWeeks ? parseInt(expectedWeeks, 10) : null,
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create cycle');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="cycle-create-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-slate-900 border border-slate-700 rounded-lg p-6 w-full max-w-md shadow-2xl my-4 sm:my-8 max-h-[90dvh] overflow-y-auto"
      >
        <h2 className="text-white text-lg font-semibold mb-1">Create cycle</h2>
        <p className="text-slate-400 text-sm mb-5">
          Declare an operational training block. You can edit or close it later.
        </p>

        <label className="block text-slate-300 text-sm mb-1">Focus</label>
        <select
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
          className="w-full mb-4 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm"
          data-testid="cycle-focus-select"
        >
          {FOCUS_OPTIONS.map((f) => (
            <option key={f} value={f}>{FOCUS_LABELS[f]}</option>
          ))}
        </select>

        <label className="block text-slate-300 text-sm mb-1">
          Note <span className="text-slate-500">(optional)</span>
        </label>
        <input
          type="text"
          value={focusNote}
          onChange={(e) => setFocusNote(e.target.value)}
          placeholder="e.g. Upper-focused, 8w accumulation"
          className="w-full mb-4 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm placeholder-slate-500"
          data-testid="cycle-note-input"
        />

        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className="block text-slate-300 text-sm mb-1">Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm"
              data-testid="cycle-start-date-input"
            />
          </div>
          <div>
            <label className="block text-slate-300 text-sm mb-1">
              Weeks <span className="text-slate-500">(optional)</span>
            </label>
            <input
              type="number"
              min="1"
              max="52"
              value={expectedWeeks}
              onChange={(e) => setExpectedWeeks(e.target.value)}
              placeholder="e.g. 8"
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm placeholder-slate-500"
              data-testid="cycle-weeks-input"
            />
          </div>
        </div>

        {error && (
          <div className="text-red-300 text-sm mb-3" data-testid="cycle-create-error">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-slate-300 hover:text-white text-sm"
            data-testid="cycle-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded text-sm font-medium"
            data-testid="cycle-create-submit-btn"
          >
            {submitting ? 'Creating…' : 'Create cycle'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CycleCreateModal;
